import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import toast from "react-hot-toast";
import { floatingIcons } from "../components/floatingIconsData";
import { useSearchParams } from "react-router-dom";

const ContactUs: React.FC = () => {

    const [searchParams] = useSearchParams();
    const companyEmail = "myticketer@gmail.com";
    const phone = "+94112229086";

    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        try {
            await axios.post(
                `https://myticketer-client-tivl.vercel.app/admin/contact`,
                {
                    email,
                    subject,
                    message,
                }
            );
            toast.success("Message sent successfully");
            setEmail("");
            setSubject("");
            setMessage("");
        } catch (error) {
            if (axios.isAxiosError(error)) {
                toast.error(error.response?.data?.message || "Failed to send message");
            } else {
                toast.error("Unexpected error occurred");
            }
        }
    };

    useEffect(() => {
        const defaultSubject = searchParams.get("subject") || "";
        const defaultMessage = searchParams.get("message") || "";

        setSubject(decodeURIComponent(defaultSubject));
        setMessage(decodeURIComponent(defaultMessage));
    }, []);

    return (
        <div className='justify-center items-center pb-20'>
            {/* Cover Section */}
            <section className="relative w-full h-[70vh] md:h-[80vh] overflow-hidden">
                <img
                    src="/contactCover.webp"
                    alt="Contact Us"
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover z-0"
                />
                <div className="absolute inset-0 bg-black/40 z-0" />
                <motion.div
                    className="relative z-10 flex flex-col items-center text-center md:text-left md:items-end md:flex-row justify-between max-w-7xl mx-auto px-6 md:px-12 h-full pb-28"
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 md:mb-0 mt-64 md:mt-0">
                        Contact Us
                    </h1>
                    <p className="text-white text-md md:text-lg max-w-xl text-center md:text-left">
                        Connect with our expert team for tailored travel support. From visa processing and ticketing to insurance and itinerary planning, we're here to assist you—every step of the way.
                    </p>
                </motion.div>
            </section>

            <section
                id="contact"
                className="relative min-h-screen bg-white text-black flex flex-col md:flex-row items-center justify-center px-6 md:px-24 overflow-hidden"
            >

                {/* Floating Background Icons */}
                <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                    {floatingIcons.map((item, index) => (
                        <div
                            key={index}
                            className="absolute text-amber-400/30 text-md animate-float"
                            style={{
                                top: item.top,
                                bottom: item.bottom,
                                left: item.left,
                                right: item.right,
                                animationDelay: item.delay,
                            }}
                        >
                            {item.icon}
                        </div>
                    ))}
                </div>

                <motion.div
                    className="w-full md:w-1/2 mb-12 md:mb-0"
                    initial={{ x: -100, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut" }}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <div className="max-w-md">
                        <div className="mb-5">
                            <img
                                src="/logo-white.webp"
                                alt="myticketer Logo"
                                width={200}
                                height={100}
                                className="object-contain"
                            />
                        </div>
                        <h2 className="text-4xl mb-4 font-bold">We’re Here to Help</h2>
                        <p className="text-gray-500">
                            Have a question or request? Reach out and our team will get back to you promptly.
                        </p>
                        <p className="mt-6">
                            <a aria-label="anchor tag"
                                href={`mailto:${companyEmail}`}
                                className="text-[#002d40] text-sm hover:underline"
                            >
                                Email: {companyEmail}
                            </a>
                        </p>
                        <p>
                            <a aria-label="anchor tag"
                                href={`tel:${phone}`}
                                className="text-[#002d40] text-sm hover:underline"
                            >
                                Phone: {phone}
                            </a>
                        </p>
                        <p className="text-[#002d40] text-sm">
                            L2-01, Realty Plaza, Ja Ela
                        </p>
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={handleSubmit}
                    className="w-full md:w-1/2 grid grid-cols-1 md:grid-cols-2 gap-6"
                    initial={{ x: 100, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
                    viewport={{ once: false, amount: 0.3 }}
                >
                    <div className="col-span-1">
                        <label htmlFor="label" className="text-sm text-gray-500 block mb-1">
                            Email Address
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="rounded w-full bg-transparent border-b border-amber-400 focus:outline-none focus:border-b-2 focus:border-amber-400 transition"
                            placeholder="example@gmail.com"
                            required
                        />
                    </div>
                    <div className="col-span-1">
                        <label htmlFor="label" className="text-sm text-gray-500 block mb-1">Subject</label>
                        <input
                            type="text"
                            value={subject}
                            onChange={(e) => setSubject(e.target.value)}
                            className="rounded w-full bg-transparent border-b border-amber-400 focus:outline-none focus:border-b-2 focus:border-amber-400 transition"
                            placeholder="Your Subject"
                            required
                        />
                    </div>
                    <div className="col-span-2">
                        <label htmlFor="label" className="text-sm text-gray-500 block mb-1">Message</label>
                        <textarea
                            rows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            className="rounded w-full bg-transparent border-b border-amber-400 focus:outline-none focus:border-b-2 focus:border-amber-400 transition resize-none"
                            placeholder="Start typing here..."
                            required
                        />
                    </div>
                    <div className="col-span-2 text-right">
                        <button aria-label="Button"
                            type="submit"
                            className="bg-amber-400 text-white text-sm cursor-pointer font-medium px-5 py-2 rounded-lg hover:bg-amber-500 transition"
                        >
                            Send
                        </button>
                    </div>
                </motion.form>
            </section>
        </div>
    );
};

export default ContactUs;
